import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import PageHeader from "../../components/PageHeader";
import SoftBox from "../../components/SoftBox";
import LoadingScreen from "../../components/LoadingScreen";
import { UseIsAdmin } from "../../hooks/auth";
import { UseCompanyColors, UseCompany, UseCompanyWorkspaces } from "../../hooks/company";
import TipTapEditor from "./TipTapEditor";
import { exportPlaybook } from "./exportPlaybook";
import {
  YETI_ASSIST,
  YETI_INLINE,
  UPLOAD_PLAYBOOK_IMAGE,
} from "../../api/playbook";
import { GET_PLAYBOOK, UPDATE_PLAYBOOK } from "./playbookStore";
import { GET_IMAGE_URL } from "../../helpers/images";
import yetiOrb from "../../img/yeti-orb.png";
import "./playbook.css";

const Icon = ({ path, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
);
const shareIcon =<><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" /></>;
const exportIcon = <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></>;
const xIcon = <path d="M18 6 6 18M6 6l12 12" />;
const checkIcon = <path d="M20 6 9 17l-5-5" />;

const esc = (s = "") => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

// "#1b598e" + 0.08 -> "rgba(27,89,142,0.08)". Falls back gracefully.
const hexToRgba = (hex, a = 1) => {
  if (typeof hex !== "string" || !hex.startsWith("#")) return `rgba(27,89,142,${a})`;
  let h = hex.slice(1);
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return `rgba(27,89,142,${a})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// Concatenate every section's pages into ONE continuous document. Each page is
// an <h1> section (the scroll-spy anchors / outline items).
const buildDoc = (bk) => {
  if (!bk?.sections?.length) return "<h1>Untitled</h1><p></p>";
  const parts = [];
  bk.sections.forEach((sec) => {
    (sec.pages || []).forEach((pg) => {
      const c = (pg.content || "").trim();
      if (/^<h1[\s>]/i.test(c)) parts.push(c);
      else parts.push(`<h1>${esc(pg.title || "Untitled")}</h1>${c || "<p></p>"}`);
    });
  });
  return parts.join("\n") || "<h1>Untitled</h1><p></p>";
};

const PlaybookBook = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = UseIsAdmin();
  const { primaryColor, secondaryColor } = UseCompanyColors();
  const { businessName } = UseCompany();

  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docHtml, setDocHtml] = useState("");
  const [outline, setOutline] = useState([]);
  const [activeHeading, setActiveHeading] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState({ template: "modern", primary: "", secondary: "", gradient: false });
  const [designOpen, setDesignOpen] = useState(false);
  const editorRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [yetiInput, setYetiInput] = useState("");
  const [yetiBusy, setYetiBusy] = useState(false);
  const yetiBodyRef = useRef(null);
  const docHtmlRef = useRef("");

  const workspaces = UseCompanyWorkspaces(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMode, setShareMode] = useState("everyone");
  const [shareWs, setShareWs] = useState([]);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    docHtmlRef.current = docHtml;
  }, [docHtml]);

  useEffect(() => {
    let alive = true;
    GET_PLAYBOOK(id)
      .then((res) => {
        if (!alive) return;
        setBook(res.data);
        setDocHtml(buildDoc(res.data));
        const t = res.data.theme || {};
        setTheme({
          template: t.template || "modern",
          primary: t.primary || "",
          secondary: t.secondary || "",
          gradient: !!t.gradient,
        });
      })
      .catch(() => toast.error("Couldn't load this playbook"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (yetiBodyRef.current) {
      yetiBodyRef.current.scrollTop = yetiBodyRef.current.scrollHeight;
    }
  }, [messages]);

  const onDocChange = (html) => {
    setDocHtml(html);
    setDirty(true);
  };

  const setTitle = (title) => {
    setBook((b) => ({ ...b, title }));
    setDirty(true);
  };

  const addSection = () => {
    setDocHtml((h) => `${h}\n<h1>New section</h1><p>Start writing…</p>`);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Persist the continuous document as a single section/page. The book's
      // structure lives in the <h1> sections inside the content.
      const res = await UPDATE_PLAYBOOK(id, {
        title: book.title,
        type: book.type,
        audience: book.audience,
        theme,
        sections: [
          {
            title: "Handbook",
            order: 0,
            pages: [{ title: book.title || "Document", content: docHtml, order: 0 }],
          },
        ],
      });
      setBook(res.data);
      setDirty(false);
      toast.success("Playbook saved");
    } catch (e) {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const runInline = async (action, selection) => {
    try {
      const res = await YETI_INLINE({ action, selection });
      return res.data?.text || "";
    } catch (e) {
      toast.error("YETI couldn't rewrite that");
      return "";
    }
  };

  const uploadImg = async (file) => {
    try {
      const res = await UPLOAD_PLAYBOOK_IMAGE(file);
      return res.data?.key ? GET_IMAGE_URL(res.data.key) : null;
    } catch (e) {
      toast.error("Couldn't upload that image");
      return null;
    }
  };

  const openShare = () => {
    const aud = book?.audience || { mode: "everyone", workspaceIds: [] };
    setShareMode(aud.mode === "workspaces" ? "workspaces" : "everyone");
    setShareWs((aud.workspaceIds || []).map(String));
    setShareOpen(true);
  };

  const toggleWs = (wsId) =>
    setShareWs((prev) =>
      prev.includes(wsId) ? prev.filter((x) => x !== wsId) : [...prev, wsId]
    );

  const doShare = async () => {
    if (shareMode === "workspaces" && !shareWs.length) {
      toast.error("Pick at least one workspace");
      return;
    }
    setSharing(true);
    try {
      const audience =
        shareMode === "everyone"
          ? { mode: "everyone", workspaceIds: [] }
          : { mode: "workspaces", workspaceIds: shareWs };
      const res = await UPDATE_PLAYBOOK(id, { audience, notify: true });
      setBook(res.data);
      setShareOpen(false);
      toast.success(
        shareMode === "everyone"
          ? "Shared with everyone — they've been notified"
          : `Shared with ${shareWs.length} workspace${shareWs.length > 1 ? "s" : ""} — they've been notified`
      );
    } catch (e) {
      toast.error("Couldn't share this playbook");
    } finally {
      setSharing(false);
    }
  };

  const doExport = () => {
    if (!book) return;
    const p = theme.primary || primaryColor || "#1b598e";
    const s = theme.secondary || secondaryColor || p;
    exportPlaybook(
      { ...book, sections: [{ title: "Handbook", pages: [{ title: book.title, content: docHtml }] }] },
      { businessName, primaryColor: p, secondaryColor: s, gradient: theme.gradient }
    );
  };

  // YETI writes into the whole handbook. With existing content the default is
  // "edit": it can rewrite a section, remove a page, add one, or reword — and
  // returns the WHOLE updated document, which replaces what's here. "create"
  // only when the handbook is empty. Callers can force "append"/"rewrite".
  const askYeti = async (promptText, mode) => {
    if (!promptText.trim() || yetiBusy) return;
    const current = docHtmlRef.current || "";
    const hasContent = !!current.trim();
    const finalMode = mode || (hasContent ? "edit" : "create");
    setMessages((m) => [...m, { role: "user", content: promptText }]);
    setYetiInput("");
    setYetiBusy(true);

    let streamed = "";
    try {
      await YETI_ASSIST(
        {
          prompt: promptText,
          mode: finalMode,
          currentContent: current,
          pageTitle: book.title,
          history: messages.slice(-6),
        },
        (full) => {
          // Guard against the model ever wrapping the HTML in a ```html fence.
          const clean = full.replace(/^\s*```(?:html)?\s*/i, "").replace(/\s*```\s*$/i, "");
          streamed = clean;
          if (finalMode === "append") {
            setDocHtml(`${current}${clean}`);
            setDirty(true);
          } else if (clean && clean.trim()) {
            // edit / rewrite / create stream the whole document — never blank
            // the editor on an empty partial.
            setDocHtml(clean);
            setDirty(true);
          }
        }
      );

      // Only claim success if something actually changed — the old panel said
      // "Done" even when nothing happened.
      const out = streamed.trim();
      let changed;
      if (finalMode === "append") changed = out.length > 0;
      else changed = out.length > 0 && out !== current.trim();

      let reply;
      if (changed && finalMode === "append") reply = "Done — added it below. Review and tweak anything by hand.";
      else if (changed) reply = "Done — I updated the handbook. Review it and tweak anything by hand.";
      else reply = "I didn't change anything — tell me exactly what to add, remove, or rewrite (name the section if you can).";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Sorry — ${e.message}` }]);
    } finally {
      setYetiBusy(false);
    }
  };

  if (loading) return <LoadingScreen height="60vh" logoHeight="120px" />;
  if (!book) return null;

  // Effective colors: the handbook's chosen theme, falling back to the brand.
  const primary = theme.primary || primaryColor || "#1b598e";
  const secondary = theme.secondary || secondaryColor || primary;
  const chrome = primaryColor || "#1b598e";
  // Chrome (Save button, outline highlight, toolbar, panels) stays on the
  // company brand. Only the DOCUMENT (--pb-doc-*) uses the chosen theme colors.
  const themeStyle = {
    "--pb-primary": chrome,
    "--pb-secondary": secondaryColor || chrome,
    "--pb-doc-primary": primary,
    "--pb-doc-secondary": secondary,
    "--pb-doc-soft": hexToRgba(primary, 0.08),
    "--pb-doc-tint": hexToRgba(primary, 0.05),
    "--pb-doc-ring": hexToRgba(primary, 0.24),
  };
  const coverBg = theme.gradient
    ? `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`
    : `radial-gradient(circle at 82% -25%, rgba(255,255,255,0.24), transparent 55%), ${primary}`;

  const patchTheme = (patch) => {
    setTheme((t) => ({ ...t, ...patch }));
    setDirty(true);
  };

  const templates = [
    { k: "modern", n: "Modern", d: "Clean and bold" },
    { k: "editorial", n: "Editorial", d: "Serif, magazine" },
    { k: "futuristic", n: "Futuristic", d: "Sleek and airy" },
  ];
  const presets = [
    { name: "Brand", primary: primaryColor || "#1b598e", secondary: secondaryColor || "#2e7bc4", gradient: false },
    { name: "Ocean", primary: "#1e5fa8", secondary: "#17b3a3", gradient: true },
    { name: "Sunset", primary: "#e0533d", secondary: "#f0a92e", gradient: true },
    { name: "Royal", primary: "#5a4bd1", secondary: "#b14bd1", gradient: true },
    { name: "Forest", primary: "#1f8f5f", secondary: "#7cc243", gradient: true },
    { name: "Slate", primary: "#334155", secondary: "#64748b", gradient: true },
  ];

  let saveLabel = "Saved";
  if (saving) saveLabel = "Saving…";
  else if (dirty) saveLabel = "Save";

  // Brand cover banner — sits at the top of the document and scrolls with it.
  const cover = (
    <div className="pb-cover" style={{ background: coverBg }}>
      <span className="pb-cover-tag">{(book.type || "Handbook").toUpperCase()}</span>
      <div className="pb-cover-title">{book.title || "Untitled handbook"}</div>
      <div className="pb-cover-meta">
        {businessName || "Your company"}
        {outline.length ? ` · ${outline.length} section${outline.length > 1 ? "s" : ""}` : ""}
      </div>
    </div>
  );

  return (
    <>
      <PageHeader headerText="Playbook" subheaderText={book.title} />
      <SoftBox className="pb-root" sx={{ px: { mini: 1, md: 3 }, pt: 0, mt: -1 }} style={themeStyle}>
        {/* The library is an admin-only surface — players open their own workspace's
            book directly and have nowhere else to go, so hide the back link for them. */}
        {isAdmin && (
          <button className="pb-btn ghost" style={{ marginBottom: 10 }} onClick={() => navigate("/playbook")}>
            <Icon path={<path d="M15 18l-6-6 6-6" />} /> All playbooks
          </button>
        )}

        <div className={`pb-shell${isAdmin ? " with-yeti" : ""}`}>
          {/* Outline (scroll-spy) */}
          <aside className="pb-rail">
            <div className="pb-rail-sec">Contents</div>
            {outline.length === 0 && <div className="pb-rail-empty">No sections yet</div>}
            {outline.map((title, i) => (
              <button
                // eslint-disable-next-line react/no-array-index-key
                key={`${title}-${i}`}
                type="button"
                className={`pb-rail-page${i === activeHeading ? " on" : ""}`}
                onClick={() => editorRef.current?.scrollToHeading(i)}
              >
                <span className="pb-rail-num">{i + 1}</span>
                <span className="pb-rail-label">{title || "Untitled"}</span>
              </button>
            ))}
            {isAdmin && (
              <button type="button" className="pb-rail-add" onClick={addSection}>
                <Icon path={<path d="M12 5v14M5 12h14" />} size={14} /> Add section
              </button>
            )}
          </aside>

          {/* Book surface — one continuous document */}
          <section className={`pb-book pb-tpl-${theme.template}`}>
            <div className="pb-book-head">
              {isAdmin ? (
                <input
                  className="pb-book-title-input"
                  value={book.title || ""}
                  placeholder="Handbook title"
                  onChange={(e) => setTitle(e.target.value)}
                />
              ) : (
                <span className="pb-book-title-input" style={{ pointerEvents: "none" }}>
                  {book.title}
                </span>
              )}
              <div className="pb-book-actions">
                <button className="pb-btn ghost" onClick={doExport} title="Export or print">
                  <Icon path={exportIcon} /> Export
                </button>
                {isAdmin && (
                  <button className="pb-btn ghost" onClick={() => setDesignOpen(true)} title="Colors and template">
                    <Icon path={<><circle cx="13.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="10.5" r="2.5" /><circle cx="8.5" cy="7.5" r="2.5" /><circle cx="6.5" cy="12.5" r="2.5" /><path d="M12 2a10 10 0 1 0 0 20 2.5 2.5 0 0 0 2-4 2.5 2.5 0 0 1 2-4h1a5 5 0 0 0 5-5 10 10 0 0 0-10-7z" /></>} /> Design
                  </button>
                )}
                {isAdmin && (
                  <button className="pb-btn ghost" onClick={openShare} title="Share with workspaces">
                    <Icon path={shareIcon} /> Share
                  </button>
                )}
                {isAdmin && (
                  <button className="pb-btn primary" onClick={save} disabled={saving || !dirty}>
                    <Icon path={<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2zM17 21v-8H7v8M7 3v5h8" />} />
                    {saveLabel}
                  </button>
                )}
              </div>
            </div>

            <div className="pb-page">
              <TipTapEditor
                ref={editorRef}
                content={docHtml}
                editable={isAdmin}
                header={cover}
                onChange={onDocChange}
                onOutline={setOutline}
                onActiveHeading={setActiveHeading}
                runInline={isAdmin ? runInline : undefined}
                onUploadImage={isAdmin ? uploadImg : undefined}
              />
            </div>
          </section>

          {/* Ask YETI (admin only) */}
          {isAdmin && (
            <aside className="pb-yeti">
              <div className="pb-yeti-head">
                <img className="pb-yeti-orb" src={yetiOrb} alt="YETI" />
                <div>
                  <div className="pb-yeti-t">Ask YETI</div>
                  <div className="pb-yeti-s">Writes into this handbook</div>
                </div>
              </div>
              <div className="pb-yeti-body" ref={yetiBodyRef}>
                {messages.length === 0 && (
                  <>
                    <div className="pb-msg yeti">Tell me what to write or change and I&apos;ll draft it — tuned to your company.</div>
                    <div className="pb-chips">
                      <button className="pb-chip" onClick={() => askYeti("Improve the writing throughout — clearer, warmer, on-brand", "rewrite")}>Improve the writing</button>
                      <button className="pb-chip" onClick={() => askYeti("Add a new section with a short FAQ", "append")}>Add an FAQ section</button>
                      <button className="pb-chip" onClick={() => askYeti("Add a section about our time-off policy", "append")}>Add a section</button>
                    </div>
                  </>
                )}
                {messages.map((m, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`${m.role}-${i}`} className={`pb-msg ${m.role === "user" ? "you" : "yeti"}`}>{m.content}</div>
                ))}
                {yetiBusy && <div className="pb-msg yeti">Writing…</div>}
              </div>
              <div className="pb-yeti-ctx">
                <Icon path={<path d="M20 6 9 17l-5-5" />} size={12} /> Using {businessName || "your company"} · its metrics
              </div>
              <div className="pb-yeti-input">
                <textarea
                  rows={1}
                  placeholder="Ask YETI to write or change the handbook…"
                  value={yetiInput}
                  onChange={(e) => setYetiInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      askYeti(yetiInput);
                    }
                  }}
                />
                <button className="pb-yeti-send" disabled={yetiBusy || !yetiInput.trim()} onClick={() => askYeti(yetiInput)}>
                  <Icon path={<path d="m5 12 14-7-7 14-2-5z" />} size={15} />
                </button>
              </div>
            </aside>
          )}
        </div>
      </SoftBox>

      {designOpen && (
        <div className="pb-modal-scrim" onClick={() => setDesignOpen(false)}>
          <div
            className="pb-root pb-modal"
            style={{ ...themeStyle, maxWidth: 540 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="pb-modal-x" onClick={() => setDesignOpen(false)} aria-label="Close">
              <Icon path={xIcon} size={18} />
            </button>
            <div className="pb-modal-inner">
              <div className="pb-modal-h">Design</div>
              <div className="pb-modal-sub">
                Pick a template and colors for this handbook. It previews live — hit Save to keep it.
              </div>

              <div className="pb-design-lbl">Template</div>
              <div className="pb-design-tpls">
                {templates.map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    className={`pb-design-tpl${theme.template === t.k ? " on" : ""}`}
                    onClick={() => patchTheme({ template: t.k })}
                  >
                    <span className="pb-design-tpl-n">{t.n}</span>
                    <span className="pb-design-tpl-d">{t.d}</span>
                  </button>
                ))}
              </div>

              <div className="pb-design-lbl">Colors</div>
              <div className="pb-design-colors">
                <label className="pb-design-swatch">
                  <input type="color" value={theme.primary || primary} onChange={(e) => patchTheme({ primary: e.target.value })} />
                  <span>Primary</span>
                </label>
                <label className="pb-design-swatch">
                  <input type="color" value={theme.secondary || secondary} onChange={(e) => patchTheme({ secondary: e.target.value })} />
                  <span>Secondary</span>
                </label>
                <label className="pb-design-grad">
                  <input type="checkbox" checked={theme.gradient} onChange={(e) => patchTheme({ gradient: e.target.checked })} />
                  <span>Gradient</span>
                </label>
              </div>

              <div className="pb-design-lbl">Presets</div>
              <div className="pb-design-presets">
                {presets.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    className="pb-design-preset"
                    title={p.name}
                    aria-label={`${p.name} color scheme`}
                    style={{ background: p.gradient ? `linear-gradient(135deg, ${p.primary}, ${p.secondary})` : p.primary }}
                    onClick={() => patchTheme({ primary: p.primary, secondary: p.secondary, gradient: !!p.gradient })}
                  />
                ))}
              </div>

              <div className="pb-modal-actions">
                <button className="pb-btn primary" onClick={() => setDesignOpen(false)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {shareOpen && (
        <div className="pb-modal-scrim" onClick={() => !sharing && setShareOpen(false)}>
          <div
            className="pb-root pb-modal"
            style={themeStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="pb-modal-x"
              onClick={() => setShareOpen(false)}
              aria-label="Close"
              disabled={sharing}
            >
              <Icon path={xIcon} size={18} />
            </button>
            <div className="pb-modal-inner">
            <div className="pb-modal-h">Share this playbook</div>
            <div className="pb-modal-sub">
              Choose who can see it on their player site. They&apos;ll get a notification that a
              handbook was shared with them.
            </div>

            <button
              type="button"
              className={`pb-share-opt${shareMode === "everyone" ? " on" : ""}`}
              onClick={() => setShareMode("everyone")}
            >
              <span className="pb-share-dot">{shareMode === "everyone" && <Icon path={checkIcon} size={12} />}</span>
              <span>
                <span className="h">Everyone</span>
                <span className="d">All players in your company can see it.</span>
              </span>
            </button>
            <button
              type="button"
              className={`pb-share-opt${shareMode === "workspaces" ? " on" : ""}`}
              onClick={() => setShareMode("workspaces")}
            >
              <span className="pb-share-dot">{shareMode === "workspaces" && <Icon path={checkIcon} size={12} />}</span>
              <span>
                <span className="h">Specific workspaces</span>
                <span className="d">Only players in the workspaces you pick.</span>
              </span>
            </button>

            {shareMode === "workspaces" && (
              <div className="pb-ws-list">
                {workspaces && workspaces.length ? (
                  workspaces.map((w) => (
                    <label key={w._id} className="pb-ws-item">
                      <input
                        type="checkbox"
                        checked={shareWs.includes(String(w._id))}
                        onChange={() => toggleWs(String(w._id))}
                      />
                      <span>{w.name || "Workspace"}</span>
                    </label>
                  ))
                ) : (
                  <div className="pb-ws-empty">No workspaces found.</div>
                )}
              </div>
            )}

            <div className="pb-modal-actions">
              <button className="pb-btn ghost" onClick={() => setShareOpen(false)} disabled={sharing}>
                Cancel
              </button>
              <button className="pb-btn primary" onClick={doShare} disabled={sharing}>
                <Icon path={shareIcon} size={15} />
                {sharing ? "Sharing…" : "Share and notify"}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlaybookBook;
